import React, { useState } from "react";

import { IconButton } from "@mui/material";

import { Relationship } from "@fartherfinance/frontend/api/Dashboard/constants/relationship";
import useDeleteHouseholdMember from "@fartherfinance/frontend/api/Households/hooks/useDeleteHouseholdMember";
import { HouseholdMemberTypeSchema } from "@fartherfinance/frontend/api/Households/schemas";
import { HouseholdId } from "@fartherfinance/frontend/api/Households/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { useHouseholdContext } from "../../context";
import { HouseholdMemberFormField } from "../../HouseholdFormFields";
import TrashCan from "@src/assets/svg/components/trashCan";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";
import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import DeleteModal from "@src/sharedComponents/Modal/DeleteModal/DeleteModal";
import Switch from "@src/sharedComponents/Switch/Switch";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import * as styles from "./HouseholdProfile.module.css";

function DeleteHouseholdMember({
  householdId,
  clientId,
}: {
  householdId: HouseholdId;
  clientId: ClientId;
}) {
  const auth = useAdvisorRequestAuth();
  const notify = useStatusNotification();
  const { mutateAsync: deleteHouseholdMember } = useDeleteHouseholdMember(auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteMember = async () => {
    if (householdId && clientId) {
      try {
        await deleteHouseholdMember({ householdId, clientId });
        setShowDeleteModal(false);
      } catch (error) {
        handleApiError({
          error: error,
          errorMessage: "Error deleting household member:",
          genericErrorMessage: "Error deleting household member",
          notify,
        });
      }
    }
  };

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <div className={styles.deleteMemberBtn}>
      <IconButton onClick={handleShowDeleteModal}>
        <TrashCan fill="var(--iconSubtle)" />
      </IconButton>
      {showDeleteModal && (
        <DeleteModal
          title="Delete Household Contact"
          text="Are you sure you want to remove this member from the household? This action cannot be undone."
          deleteButtonText="Delete"
          onDelete={handleDeleteMember}
          onClose={handleCloseDeleteModal}
        />
      )}
    </div>
  );
}

const newMemberRelationship = {
  validate: {
    isRelationshipRequiredWhenMemberSelected: (
      value: EnhancedAutocompleteOption<Relationship> | undefined,
      formData: Record<string, any>
    ): boolean | string => {
      const memberOption = formData?.newMember?.memberOption;

      // If no member is selected, relationship is optional
      if (!memberOption) {
        return true;
      }

      // If member is selected, relationship is required
      if (memberOption && !value) {
        return "Relationship is required when a member is selected";
      }

      return true;
    },
  },
};

export function HouseholdMemberRelationshipFormField({
  memberName,
  relationshipName,
  pointOfContactName,
  className,
  withDeleteButton = false,
  householdId,
  clientId,
  memberLabel = "Name",
  customAction,
}: {
  memberName: string;
  relationshipName: string;
  pointOfContactName?: string;
  className?: string;
  withDeleteButton?: boolean;
  householdId?: HouseholdId;
  clientId?: ClientId;
  memberLabel?: string;
  customAction?: React.ReactNode;
}) {
  const { clientsOptions, clientsQuery, form } = useHouseholdContext();

  const { watch, setValue } = form;

  return (
    <BorderBox variant="bold" className={className}>
      <div className={styles.memberFormRow}>
        <div className={styles.memberNameField}>
          <HouseholdMemberFormField
            name={memberName}
            label={memberLabel}
            clientsOptions={clientsOptions}
            clientsQuery={clientsQuery}
          />
        </div>
        <div className={styles.memberRelationshipField}>
          <FormDropdownField
            name={relationshipName}
            label="Relationship"
            values={HouseholdMemberTypeSchema.options
              .map((option) => ({
                label: option,
                value: option,
              }))
              .filter((option) => option.value !== "Owner")}
            disablePortal={false}
            rules={newMemberRelationship}
          />
        </div>
        <div className={styles.memberActionsField}>
          {customAction ? (
            <div className={styles.addContactActionsColumn}>
              {pointOfContactName && (
                <div className={styles.pointOfContactSection}>
                  <Text variant="body">Point of Contact</Text>
                  <Switch
                    on={watch(pointOfContactName as any) || false}
                    onClick={() => {
                      const currentValue =
                        watch(pointOfContactName as any) || false;
                      setValue(pointOfContactName as any, !currentValue);
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              {pointOfContactName && (
                <div className={styles.pointOfContactSection}>
                  <Text variant="body">Point of Contact</Text>
                  <Switch
                    on={watch(pointOfContactName as any) || false}
                    onClick={() => {
                      const currentValue =
                        watch(pointOfContactName as any) || false;
                      setValue(pointOfContactName as any, !currentValue);
                    }}
                  />
                </div>
              )}
              {pointOfContactName && withDeleteButton && (
                <div className={styles.verticalDivider} />
              )}
              {withDeleteButton && (
                <DeleteHouseholdMember
                  householdId={householdId!}
                  clientId={clientId!}
                />
              )}
            </>
          )}
        </div>
      </div>
      {customAction !== undefined && (
        <div className={styles.customActionRow}>{customAction}</div>
      )}
    </BorderBox>
  );
}
